import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Image } from "antd";
import { useRef, useState } from "react";
import Helper from "../../../Helper";
import { WaypointStep } from "../../../models/WaypointStep";
import { convertDistanceToReadable } from "../../Helper/WaypointGuidance";
import WaypointManeuverIcon from "./WaypointManeuverIcon";

const SWIPE_THRESHOLD_PX = 50;

interface WaypointGuidanceBannerProps {
    waypointStep: WaypointStep;
    nextWaypointStep: WaypointStep | null;
    stepIndex: number;
    totalSteps: number;
    // Null when location is off, since a distance from nowhere would be misleading.
    distanceMeters: number | null;
    changeStep: (step: number) => void;
    // Another route follows this one, so the last step can still move forward onto it.
    hasContinuation: boolean;
}

export default function WaypointGuidanceBanner({
    waypointStep,
    nextWaypointStep,
    stepIndex,
    totalSteps,
    distanceMeters,
    changeStep,
    hasContinuation,
}: WaypointGuidanceBannerProps) {
    const canGoNext = stepIndex < totalSteps || hasContinuation;
    const [pictureOpen, setPictureOpen] = useState(false);
    const touchStartX = useRef<number | null>(null);

    const onTouchEnd = (event: React.TouchEvent) => {
        if (touchStartX.current == null) {
            return;
        }
        const deltaX = event.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(deltaX) >= SWIPE_THRESHOLD_PX) {
            changeStep(stepIndex + (deltaX < 0 ? 1 : -1));
        }
    };

    const pagerButtonClassName = (disabled: boolean) =>
        Helper.classNames(
            "w-10 h-10 flex items-center justify-center rounded-full",
            disabled ? "text-gray-300 cursor-not-allowed" : "text-plot-slate hover:bg-gray-100 cursor-pointer"
        );

    return (
        <div className="absolute top-0 inset-x-0 z-10 p-2 flex flex-col gap-1.5 pointer-events-none">
            <div
                className="pointer-events-auto rounded-2xl overflow-hidden shadow-xl select-none"
                onTouchStart={(event) => (touchStartX.current = event.touches[0].clientX)}
                onTouchEnd={onTouchEnd}
            >
                {/* Dark text: white on PLOT orange is too low-contrast to read at a glance. */}
                <div className="flex items-center gap-4 p-4 bg-plot-orange text-plot-slate">
                    <div className="w-14 h-14 shrink-0 flex items-center justify-center rounded-xl bg-plot-slate text-white text-3xl">
                        <WaypointManeuverIcon waypointStep={waypointStep} stepIndex={stepIndex} />
                    </div>
                    <div className="flex-1 min-w-0">
                        {distanceMeters != null && (
                            <div className="text-3xl font-extrabold tabular-nums leading-none mb-1">
                                {convertDistanceToReadable(distanceMeters)}
                            </div>
                        )}
                        <p
                            className={Helper.classNames(
                                "font-semibold leading-snug line-clamp-3",
                                distanceMeters != null ? "text-lg" : "text-xl"
                            )}
                        >
                            {waypointStep.instruction || `Step ${stepIndex}`}
                        </p>
                    </div>
                    {waypointStep.pictureUrl && (
                        <button
                            type="button"
                            aria-label="View step photo"
                            onClick={() => setPictureOpen(true)}
                            className="w-16 h-16 shrink-0 rounded-xl overflow-hidden ring-2 ring-white cursor-zoom-in"
                        >
                            <img
                                src={waypointStep.pictureUrl}
                                alt={waypointStep.instruction || `Step ${stepIndex}`}
                                className="w-full h-full object-cover"
                            />
                        </button>
                    )}
                </div>

                {(nextWaypointStep || hasContinuation) && (
                    <div className="flex items-center gap-2 bg-plot-slate text-white text-sm px-4 py-2">
                        <span className="text-white/70 shrink-0">Then</span>
                        {nextWaypointStep ? (
                            <>
                                <span className="shrink-0">
                                    <WaypointManeuverIcon waypointStep={nextWaypointStep} stepIndex={stepIndex + 1} />
                                </span>
                                <span className="truncate font-medium">{nextWaypointStep.instruction}</span>
                            </>
                        ) : (
                            <span className="truncate font-medium">continue to the dropoff</span>
                        )}
                    </div>
                )}
            </div>

            <div className="flex justify-end">
                <div className="pointer-events-auto shrink-0 flex items-center rounded-full bg-white shadow-md text-sm">
                    <button
                        type="button"
                        aria-label="Previous step"
                        disabled={stepIndex === 1}
                        onClick={() => changeStep(stepIndex - 1)}
                        className={pagerButtonClassName(stepIndex === 1)}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <span className="text-plot-slate font-medium tabular-nums">
                        {stepIndex} of {totalSteps}
                    </span>
                    <button
                        type="button"
                        aria-label="Next step"
                        disabled={!canGoNext}
                        onClick={() => changeStep(stepIndex + 1)}
                        className={pagerButtonClassName(!canGoNext)}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>

            {waypointStep.pictureUrl && (
                <Image
                    style={{ display: "none" }}
                    alt={waypointStep.instruction || `Step ${stepIndex}`}
                    src={waypointStep.pictureUrl}
                    preview={{
                        visible: pictureOpen,
                        src: waypointStep.pictureUrl,
                        onVisibleChange: setPictureOpen,
                    }}
                />
            )}
        </div>
    );
}
